import { IEvent } from '@/app/shared/interfaces/event.interface';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@core/environments/environment';
import { IUserRole } from '@core/interfaces/user.interface';
import { EDeliverableType } from '@enums/campaign.enum';
import { ERoles } from '@enums/roles.enum';
import * as Sentry from '@sentry/angular';
import { toEventDeliverableType } from '@utilities/event-payload-mapping.util';
import { v4 as uuidv4 } from 'uuid';
import { AuthService } from './auth.service';

const SESSION_ID_STORAGE_KEY = 'analyticsEventSessionId';

// Precedence for user_role ("always highest"), confirmed 2026-07-29.
const ROLE_PRIORITY: ERoles[] = [
  ERoles.admin,
  ERoles.system,
  ERoles.support,
  ERoles.annotator,
  ERoles.campaign,
  ERoles.campaignSub,
  ERoles.creator,
];

/**
 * Fires product-analytics events (see docs/event-tracking-implementation-map.md). Fire-and-forget —
 * callers never need to subscribe or handle errors themselves.
 *
 * - user_group_id is required by the backend schema but there's no logged-in user on no-login
 *   briefing/report views. Sourced with this precedence: explicit `properties.userGroupId` > the
 *   logged-in user's own userGroupId > the campaign-context fallback set via setCampaignContext()
 *   (for no-login views, so deeply-nested components don't each need it threaded in as a prop).
 *   A call site can still pass it explicitly if setCampaignContext() hasn't run yet at that point.
 * - Any explicit field in `properties` overrides the auto-filled default (id, eventType, timestamp,
 *   sessionId, userId, userRole, userGroupId, campaignId).
 * - Requests are only sent when environment.eventTrackingEnabled is true (see
 *   src/app/core/environments/environment.defaults.ts).
 */
@Injectable({
  providedIn: 'root',
})
export class EventTrackingService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private sessionId?: string;
  private currentCampaignId: string | null = null;
  private currentUserGroupId: string | null = null;

  /**
   * Set/cleared by campaign-details.component.ts (once the real campaign id is confirmed, and on
   * destroy) — lets deeply-nested shared components (e.g. grouped-datatable) fire campaign-scoped
   * events without threading campaignId (or, for no-login briefing/report views, userGroupId) as a
   * prop through every intermediate component. Must be cleared to null when leaving campaign
   * context, so a stale value from a previous campaign never leaks into an event fired somewhere
   * unrelated. userGroupId here is only ever a fallback — an explicit `properties.userGroupId` or a
   * logged-in user's own userGroupId always wins (see buildEvent()).
   */
  public setCampaignContext(campaignId: string | null, userGroupId: string | null = null): void {
    this.currentCampaignId = campaignId;
    this.currentUserGroupId = userGroupId;
  }

  public track(eventType: IEvent['eventType'], properties: Partial<IEvent> = {}): void {
    const event = this.buildEvent(eventType, properties);

    if (!event) {
      return;
    }

    this.send(event);
  }

  private buildEvent(eventType: IEvent['eventType'], properties: Partial<IEvent>): IEvent | null {
    const currentUser = AuthService.currentDbUser;

    const event: IEvent = {
      campaignId: this.currentCampaignId ?? undefined,
      eventType,
      id: uuidv4(),
      sessionId: this.getSessionId(),
      timestamp: new Date().toISOString(),
      userGroupId: currentUser?.userGroupId ?? this.currentUserGroupId ?? '',
      userId: currentUser?.id,
      userRole: currentUser ? this.getHighestRole(currentUser.roles) : undefined,
      ...properties,
    };

    if (event.creatorId?.includes(',') && event.creatorId.split(',').length > 1) {
      // Backend requires a single valid UUID - a shared/group CTA's comma-joined creatorId list
      // (see e.g. creators-and-ctas.component.ts's [...creatorIds].sort().join(',')) 400s there.
      // Omit rather than send the first id or similar, since that would misattribute the event to
      // one specific creator. Multi-id support, if ever added backend-side, replaces this check.
      event.creatorId = undefined;
    }

    if (!event.userGroupId) {
      // eslint-disable-next-line no-console
      console.warn(`[EventTrackingService] Dropped "${event.eventType}": no userGroupId (backend requires it)`);

      // Only report to Sentry once ingestion is actually live — no point paging on a system that
      // isn't sending anything yet. Confirmed 2026-07-29: the no-login briefing/export routes
      // (campaigns/briefing/creator/{id}, campaigns/export/{id}) already return `user_group_id` on
      // their `Campaign` response, so a real no-login call site simply passing that value through
      // (see `campaign.userGroupId` usage in campaign-details.component.ts) should never hit this
      // branch in practice — this is a safety net for a call site that forgets to, not a known gap.
      if (environment.eventTrackingEnabled) {
        this.reportInvalidEvent(event);
      }

      return null;
    }

    if (event.deliverableType) {
      // EDeliverableType's values are camelCase; the backend's Deliverable enum is snake_case (and
      // not always a direct transform - see toEventDeliverableType). Converted centrally here so no
      // call site needs to remember to do it.
      event.deliverableType = toEventDeliverableType(event.deliverableType) as EDeliverableType;
    }

    return event;
  }

  private send(event: IEvent): void {
    if (environment.eventTrackingConsoleLogsEnabled) {
      // eslint-disable-next-line no-console
      console.log('[EventTrackingService]', event.eventType, event);
    }

    if (!environment.eventTrackingEnabled) {
      return;
    }

    this.http.post(this.getEventsApiUrl(), [event]).subscribe({
      error: (error: unknown) => {
        Sentry.withScope((scope) => {
          scope.setExtra('eventType', event.eventType);
          scope.setExtra('event', event);
          Sentry.captureException(error);
        });
      },
    });
  }

  // Mirrors ImageUtilitiesService.getApiUrl() - the backend has three separate endpoints (no-login
  // briefing/export views can't authenticate against the plain one), routed off the current URL
  // the same way image/file requests already are for these exact route contexts.
  private getEventsApiUrl(): string {
    const { url } = this.router;

    if (url.includes('campaigns/briefing/creator')) {
      return `/events/briefing/${url.split('/').pop()}`;
    }

    if (url.includes('campaigns/export')) {
      return `/events/export/${url.split('/').pop()}`;
    }

    return '/events';
  }

  private reportInvalidEvent(event: IEvent): void {
    Sentry.withScope((scope) => {
      scope.setExtra('eventType', event.eventType);
      scope.setExtra('event', event);
      Sentry.captureException(new Error(`Analytics event "${event.eventType}" fired without a userGroupId`));
    });
  }

  private getSessionId(): string {
    if (this.sessionId) {
      return this.sessionId;
    }

    // track() is called from constructors and click handlers: blocked storage (privacy settings) must
    // not throw into them, so the session id then lives for this page load only.
    try {
      this.sessionId = sessionStorage.getItem(SESSION_ID_STORAGE_KEY) ?? crypto.randomUUID();
      sessionStorage.setItem(SESSION_ID_STORAGE_KEY, this.sessionId);
    } catch {
      this.sessionId ??= crypto.randomUUID();
    }

    return this.sessionId;
  }

  private getHighestRole(roles: IUserRole[]): string | undefined {
    const highest = ROLE_PRIORITY.find((role) => roles.some((userRole) => userRole.id === (role as string)));

    return highest ?? roles[0]?.id;
  }
}
